import { DateValue, getLocalTimeZone, today } from "@internationalized/date";
import { SearchOutline24Icon } from "@vygruppen/spor-icon-react";
import { format } from "date-fns/format";
import { OploggSendMessage } from "features/TodoOploggSidebar/Oplogg/OploggSendMessage";
import { useOplogg } from "features/TodoOploggSidebar/Oplogg/useOplogg";
import { SearchResultText } from "features/VehicleSidebar/VehicleList/common/SearchResultText";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DropsDatePicker,
  Field,
  Flex,
  SearchInput,
  Separator,
  TimePicker,
  Tooltip,
  VStack,
} from "shared/components/Spor";
import { SectionButton } from "shared/components/buttons/SectionButton";
import { sum } from "shared/utils/arrayUtils";
import styled, { useTheme } from "styled-components";
/**
 * Necessary in order to hide time display (show --:--). This is because if
 * you try to hide the time (aka time == null) with Spor's TimePicker, it
 * disables(!) the TimePicker completely, which renders it unusable.
 */
const TimePickerHolder = styled(Flex)<{ time: string | null }>`
  &&& {
    /* Necessary in order for the TimePicker to take the necessary space when time != null  */
    flex: 1;
    time {
      width: 100%;
    }
    .spor-field__root > * {
      width: 100%;
    }
  }
  ${({ time, theme }) =>
    !time &&
    `
  div[role="spinbutton"] {
    position: relative;
    color: transparent; /* hide existing content */
    margin: 0px 10px 0px 2px;
  }

  div[role="spinbutton"]::after {
    content: "––";
    position: absolute;
    left: 0;
    font-size: 18px;
    color: ${theme.colorTextMain};
  }
  `};
`;

export const OpLoggInput = () => {
  const { setSelectedDateTime, setSearchString, searchString, data } =
    useOplogg();
  const theme = useTheme();
  const { t } = useTranslation();

  const [date, setDate] = useState<DateValue | null>(null);
  const [time, setTime] = useState<string | null>(null);

  // change date when you pass midnight
  function handleChangeTime(newTime: string | undefined) {
    if (searchString) setTime(null);

    const selectedTime = time ? time.slice(0, 5) : "00:00";
    const newTimeFormatted = newTime ? newTime.slice(0, 5) : "00:00";

    if (date && /^00:/.test(selectedTime) && /^23:/.test(newTimeFormatted)) {
      setDate(date.subtract({ days: 1 }));
    } else if (
      date &&
      /^23:/.test(selectedTime) &&
      /^00:/.test(newTimeFormatted)
    ) {
      setDate(date.add({ days: 1 }));
    }

    setTime(newTimeFormatted);
  }

  const [messaging, setMessaging] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);

  useEffect(() => {
    if (time && date) {
      setSelectedDateTime(
        `${format(date.toDate(getLocalTimeZone()), "yyyy-MM-dd")}T${time}:00`,
      );
    } else if (date) {
      setSelectedDateTime(
        format(date.toDate(getLocalTimeZone()), "yyyy-MM-dd"),
      );
    } else if (time) {
      const todaysDate = today(getLocalTimeZone());
      setDate(todaysDate);
    }
  }, [date, time]);

  useEffect(() => {
    if (searchString) {
      setTime(null);
    }
  }, [searchString]);

  return (
    <VStack gridArea="input" alignItems="space-between" p="12px" gap="12px">
      <Flex
        pt="12px"
        pb="6px"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        <SectionButton
          variant="primary"
          checked={messaging}
          onCheckedChange={() => {
            setMessaging(!messaging);
            setSearching(false);
          }}
          label={t("Ny melding")}
          height="42px"
        />
        <SectionButton
          checked={searching}
          onCheckedChange={() => {
            if (searching) {
              setSelectedDateTime(null);
              setSearchString("");
              setDate(null);
              setTime(null);
            }
            setSearching(!searching);
            setMessaging(false);
          }}
          alerts={0}
          icon={<SearchOutline24Icon />}
          label={t("Søk")}
          height="42px"
          fontSize="18px"
        />
      </Flex>

      {searching && (
        <>
          <Separator
            mx="-12px"
            w="calc(100% + 24px)"
            borderTop={`1px solid ${theme.colorOutlineSecondary}`}
          />
          <Field>
            <SearchInput
              mb="6px"
              label="Søk etter strekning, type hendelse..."
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
            />
            <Flex gap="9px">
              <TimePickerHolder time={time}>
                <Tooltip
                  label={
                    searchString !== ""
                      ? "Tøm søkefeltet for å bruke tidsvelgeren"
                      : undefined
                  }
                  hasArrow
                >
                  <TimePicker
                    disabled={searchString !== ""}
                    label="Tidspunkt"
                    minuteInterval={60}
                    onValueChange={(newTime) =>
                      handleChangeTime(newTime?.toString())
                    }
                  />
                </Tooltip>
              </TimePickerHolder>
              <DropsDatePicker
                label="Dato"
                variant="core"
                value={date}
                width="50%"
                minValue={today(getLocalTimeZone()).subtract({ days: 7 })}
                maxValue={today(getLocalTimeZone()).add({ days: 1 })}
                onValueChange={(value) => {
                  setDate(value ?? today(getLocalTimeZone()));
                }}
                showYearNavigation={false}
                withPortal
              />
            </Flex>
          </Field>
        </>
      )}
      {searchString.length >= 2 && data && (
        <SearchResultText
          searchHits={sum(data.pages.flatMap((it) => it.entries.length))}
          cancelSearch={() => setSearchString("")}
        />
      )}

      {messaging && <OploggSendMessage setMessaging={setMessaging} />}
    </VStack>
  );
};
